import { isBigCompanyByUser, isCompanyType } from "@/constants/vip";
import useAppStore from "@/context/store";
import { goGdMerge } from "@/pageComponents/GdMerge";
import { ORGS_CLASS_MAP, TASK_STATUS } from "@/pageComponents/GdMerge/shared";
import {
  checkCurrentTask,
  checkMergeResult,
  createGdAccuntAndMerge,
  getGdUserOrgs,
  registerGd,
} from "@/services";
import { useAsyncEffect, useMemoizedFn } from "ahooks";
import { debounce } from "lodash";
import { useRouter } from "next/router";
import { applinkColdBoot, applinkMerge } from "./applink";
import { getAccessToken } from "./auth";
import { useAutoRetryFn } from "./autoRetryFnWrap";
import { getOrgIdByUser, isGdMerged } from "./gaoding";
import { openBindTelModal } from "./open";
import useReq from "./useReq";

/**
 * 登录后自动合并账号
 * @param openMergeDialog 此手机号已有稿定账号时， 是否打开合并弹窗
 * @returns
 */
export function useLoginAutoMerge(openMergeDialog?: boolean) {
  // 轮询任务
  const loopTask = useAutoRetryFn({
    fn: async (taskId: number) => {
      if (!taskId) {
        // taskId不存在，不轮询
        return true;
      }
      const res = await checkMergeResult({ taskId });

      if (res?.status === TASK_STATUS.success) {
        // 合并成功更新用户信息
        await useAppStore.getState().refreshUser();
        return true;
      }

      return false;
    },

    retryInterval: 300,
  });

  // 通过创建稿定账号来合并
  const mergeByCreateGdAccount = useMemoizedFn(async (user) => {
    const orgClass = isCompanyType(user)
      ? ORGS_CLASS_MAP.ORG
      : ORGS_CLASS_MAP.PERSONAL;

    const result: any = await createGdAccuntAndMerge({
      /**组织类型 */
      init_org_type: orgClass,
      /**花瓣用户名 */
      hb_user_name: user?.username,
    });

    if (result?.task_id) {
      // 清除已有的轮询，避免多个轮询
      loopTask.clear();
      return loopTask(result?.task_id);
    }
  });

  const { runAsync } = useReq(
    async (user?: IUser) => {
      user = user || useAppStore.getState().user;

      if (localStorage.getItem("skipMerge") === "1") {
        // 跳过一体化合并
        return;
      }

      if (isGdMerged(user) || isBigCompanyByUser(user)) {
        return;
      }

      if (user?.tel) {
        // 有手机号，调用花瓣后端接口，自动使用手机号注册稿定账号
        const result = await registerGd();

        if (result?.task?.task_id) {
          //返回任务id，说明正在注册
          loopTask.clear();
          return loopTask(result.task.task_id);
        } else if (result.gd_user) {
          if (openMergeDialog) {
            // 全局合并弹窗
            return true;
          } else {
            return mergeByCreateGdAccount(user);
          }
        }
      } else {
        // 无手机号场景，自动注册稿定账号
        return mergeByCreateGdAccount(user);
      }
    },
    {
      manual: true,
    }
  );

  return runAsync;
}

export function useGlobalMerge() {
  const autoMerge = useLoginAutoMerge(false);
  const router = useRouter();

  const { user, config } = useAppStore(({ user, config }) => ({
    user,
    config,
  }));

  useAsyncEffect(async () => {
    if (
      [
        "/oauth/authorize",
        "/login",
        "/signup",
        "/bind/tel",
        "/oauth/callback",
        "/share",
        "/gdmerge",
        "/",
      ].includes(router.pathname)
    ) {
      // 授权等页面不做操作
      return;
    }

    if (localStorage.getItem("skipMerge") === "1") {
      // 跳过一体化合并
      return;
    }

    if (
      user &&
      config?.globalMerge &&
      !config?.mergeWhiteUser?.includes(String(user?.user_id))
    ) {
      const accessToken = await getAccessToken();

      if (accessToken) {
        // 有uc_id， 走统一账号初始化流程
        await unifiedAccountInit(user);
        return;
      }

      if (isGdMerged(user) || isBigCompanyByUser(user)) {
        return;
      }

      const check = await checkCurrentTask();

      if (check?.task_id) {
        return;
      }

      const mergeResult = await goGdMerge({
        needGlobalControl: true,
        needAutoMerge: false,
      });

      if (!mergeResult) {
        // 没有手机号，打开绑定手机号弹窗
        if (!user.tel) {
          await openBindTelModal({
            cancelNeedResolve: true,
          });
        }
        await autoMerge();
      }
    }
  }, [user?.user_id, router]);
}

const unifiedAccountInit = debounce(
  async function (user: IUser) {
    let orgId = getOrgIdByUser(user);
    if (!orgId) {
      // 走合并流程
      const res = await applinkMerge();
      if (!res) {
        return;
      }

      // 没有组织, 获取租户列表
      const orgs = await getGdUserOrgs();
      if (orgs.length === 0) {
        // 没有租户， 打开冷启动弹窗
        await applinkColdBoot();
      }
      useAppStore.setState({
        orgList: orgs,
      });
      // 合并成功更新用户信息
      await useAppStore.getState().refreshUser();
    }
  },
  500,
  {
    leading: true,
  }
);
